import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  Phone, Mail, Award, ShieldCheck, CheckCircle, ArrowRight,
  Wrench, Home, Building2, Calendar, Droplets, Flame, Gauge, Zap, Heart
} from 'lucide-react';

const PHONE = "403-615-8770";
const EMAIL = "Radinovicw@shaw.ca";

const milestones = [
  { year: '2009', label: 'DNB founded — serving Calgary' },
  { year: '25+', label: 'Years of trade experience' },
  { year: '100%', label: 'Owner-operated' },
];

const values = [
  {
    icon: ShieldCheck,
    title: 'Honest Service',
    text: 'Straight answers, fair pricing, and recommendations that genuinely fit your situation — no upselling.'
  },
  {
    icon: Wrench,
    title: 'Clean Workmanship',
    text: 'Neat installs, tidy job sites, and respect for your home or property from start to finish.'
  },
  {
    icon: Heart,
    title: 'Practical Solutions',
    text: 'We focus on doing the job properly the first time, so you don\'t have us back for the same issue.'
  },
  {
    icon: Home,
    title: 'Long-Term Relationships',
    text: 'We earn customers for life by treating every job — big or small — as if it were our own home.'
  },
];

const expertise = [
  { icon: Droplets, label: 'Plumbing', desc: 'Repairs, renovations, water heaters, Poly-B, drains' },
  { icon: Flame, label: 'Heating', desc: 'Furnace, boilers, hydronic, in-floor radiant' },
  { icon: Gauge, label: 'Gas Fitting', desc: 'Lines, BBQ, fireplaces, appliances, safety testing' },
];

const credentials = [
  'Journeyman plumber & gas fitter',
  'Fully insured & WCB-covered',
  'Residential & light commercial',
  '25+ years hands-on experience',
  'Owner-operated — you deal with Mike',
  'Local Calgary contractor since 2009',
];

export const AboutPage = () => {
  return (
    <>
      <Helmet>
        <title>About DNB Plumbing | Mike Radinovic, Calgary Plumber Since 2009</title>
        <meta
          name="description"
          content="Meet Mike Radinovic, owner of DNB Plumbing, Heating & Gas Fitting Inc.. Journeyman plumber with 25+ years of experience, serving Calgary and area since 2009."
        />
        <link rel="canonical" href="/about" />
      </Helmet>

      <main data-testid="about-page" className="page-transition">
        {/* Hero */}
        <section className="relative overflow-hidden bg-white pt-20 pb-24">
          
          

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto text-center"
            >
              <span className="text-[#0284C7] uppercase tracking-widest text-sm font-semibold">About DNB</span>
              <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-[#0F172A] mt-4 mb-6 leading-tight">
                Local. Experienced.
                <br />
                <span className="text-[#0284C7]">
                  Hands-on contractor.
                </span>
              </h1>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
                DNB Plumbing, Heating &amp; Gas Fitting Inc. is an owner-operated Calgary business built on honest service, clean workmanship, and practical solutions for residential and light commercial customers.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Owner Story */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
              {/* Photo / Stat Card */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <div className="relative">
                  
                  <div className="relative bg-white rounded-3xl p-10 border border-slate-200 shadow-xl shadow-slate-200/50">
                    <div className="text-center">
                      <div className="relative w-36 h-36 mx-auto mb-6">
                        
                        <div className="relative w-36 h-36 bg-[#0F172A] rounded-full flex items-center justify-center shadow-xl">
                          <Wrench className="w-20 h-20 text-white" strokeWidth={1} />
                        </div>
                      </div>
                      <h3 className="font-heading font-extrabold text-2xl text-[#0F172A]">
                        Mike Radinovic
                      </h3>
                      <p className="text-[#0284C7] font-semibold leading-tight mt-1">
                        Owner &amp; Lead Tradesman
                      </p>
                      <p className="text-slate-500 text-sm mt-1 mb-6">
                        Journeyman Plumber &amp; Gas Fitter
                      </p>

                      <div className="grid grid-cols-3 gap-4 pt-6 border-t border-slate-200">
                        {milestones.map((m) => (
                          <div key={m.year}>
                            <div className="text-3xl font-heading font-extrabold text-[#0F172A]">
                              {m.year}
                            </div>
                            <div className="text-xs text-slate-500 mt-1 leading-tight">{m.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Story */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <span className="text-[#0284C7] uppercase tracking-widest text-sm font-semibold">Meet the Owner</span>
                <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-[#0F172A] mt-3 mb-6 leading-tight">
                  Doing the job properly, the first time.
                </h2>
                <div className="space-y-4 text-slate-600 leading-relaxed">
                  <p>
                    At DNB Plumbing, Heating &amp; Gas Fitting Inc., owner <strong className="text-[#0F172A]">Mike Radinovic</strong> brings <strong>over 25 years of hands-on trade experience</strong> to every job. Serving Calgary and the surrounding area since 2009, DNB has built its reputation on honest service, clean workmanship, and practical solutions for residential and light commercial customers.
                  </p>
                  <p>
                    As a <strong>journeyman plumber and gas fitter</strong> with extensive experience in plumbing, heating, and gas fitting, Mike understands the importance of doing the job properly the first time. Whether it&rsquo;s a repair, renovation, water heater replacement, boiler or hydronic heating work, gas line installation, or general mechanical service, DNB focuses on reliable work, clear communication, and treating every home or business with respect.
                  </p>
                  <p>
                    DNB is small and local on purpose. When you call, you&rsquo;re dealing directly with Mike &mdash; not a dispatcher, not a salesperson. That means clearer answers, fair pricing, and accountability on every job.
                  </p>
                </div>

                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {credentials.map((c) => (
                    <div key={c} className="flex items-center gap-2.5">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-slate-100 text-[#0284C7] flex-shrink-0">
                        <CheckCircle className="w-4 h-4" />
                      </span>
                      <span className="text-slate-700 text-sm font-medium">{c}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* What we do */}
        <section className="py-20 section-tint">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <span className="text-[#0284C7] uppercase tracking-widest text-sm font-semibold">What We Do</span>
              <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-[#0F172A] mt-3 mb-4">
                Full-service plumbing, heating &amp; gas fitting
              </h2>
              <p className="text-slate-600 max-w-2xl mx-auto">
                Residential and light commercial work, from quick repairs to renovation rough-ins and complete system replacements.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {expertise.map((e, i) => (
                <motion.div
                  key={e.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="water-card p-8 text-center"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-slate-100 border border-slate-200 mb-5">
                    <e.icon className="w-8 h-8 text-[#0284C7]" strokeWidth={1.4} />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-[#0F172A] mb-2">{e.label}</h3>
                  <p className="text-slate-600 text-sm">{e.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Values / How we work */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <span className="text-[#0284C7] uppercase tracking-widest text-sm font-semibold">How We Work</span>
              <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-[#0F172A] mt-3 mb-4">
                What you can expect from DNB
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((v, i) => (
                <motion.div
                  key={v.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="p-7 rounded-2xl bg-slate-50 border border-slate-200 hover:shadow-lg hover:shadow-slate-200 transition-all"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white border border-slate-200 mb-4">
                    <v.icon className="w-6 h-6 text-[#0284C7]" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-[#0F172A] mb-2">{v.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{v.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Electrical Partner Section */}
        <section className="py-20 section-tint">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative bg-white rounded-3xl p-10 lg:p-14 border border-slate-200 shadow-lg shadow-slate-200/40 overflow-hidden"
            >
              

              <div className="relative grid grid-cols-1 lg:grid-cols-3 gap-10 items-center">
                <div className="lg:col-span-2">
                  <span className="text-[#0284C7] uppercase tracking-widest text-sm font-semibold">Our Trusted Partner</span>
                  <h2 className="font-heading font-extrabold text-3xl text-[#0F172A] mt-3 mb-4 leading-tight">
                    For electrical work, we trust <span className="text-[#0284C7]">Brandon at YYC Electrical Solutions</span>.
                  </h2>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    Plumbing, heating, and gas fitting work often involves electrical &mdash; new water heater wiring, boiler controls, garage heater circuits, thermostat hookups, dedicated appliance lines, and more. For that work, DNB partners with <strong className="text-[#0F172A]">Brandon, owner of YYC Electrical Solutions</strong>.
                  </p>
                  <p className="text-slate-600 leading-relaxed mb-6">
                    Brandon brings the same standards we do &mdash; clean work, fair pricing, and reliable service. If your project needs both mechanical and electrical work coordinated together, you&rsquo;re in great hands with the DNB &amp; YYC Electrical Solutions team.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {['Licensed Electrician', 'Calgary-Local', 'Mechanical Integration', 'Trusted by DNB'].map((tag) => (
                      <span key={tag} className="px-3 py-1.5 bg-slate-100 text-[#0369A1] text-xs font-semibold rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex justify-center">
                  <div className="text-center p-7 bg-slate-50 rounded-2xl border border-slate-200 max-w-xs w-full">
                    <div className="aspect-square flex items-center justify-center bg-white rounded-xl mb-4 border border-slate-100">
                      <Zap className="w-20 h-20 text-[#0284C7]" strokeWidth={1.3} />
                    </div>
                    <p className="text-[#0F172A] font-heading font-bold">YYC Electrical Solutions</p>
                    <p className="text-sm text-slate-500 mt-1">Master Electrician &middot; Brandon</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 cta-banner relative">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-white mb-4">
              Let&rsquo;s talk about your project
            </h2>
            <p className="text-slate-300/90 mb-8 max-w-xl mx-auto text-lg">
              Get a free, no-pressure estimate from Mike. We&rsquo;ll be straight with you about scope, timing, and price.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-[#0F172A] font-heading font-bold uppercase tracking-wider px-8 py-4 rounded-full hover:shadow-xl hover:shadow-slate-300/30 transition-all inline-flex items-center justify-center gap-2"
              >
                Request a Free Estimate
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href={`tel:${PHONE}`}
                className="border-2 border-white text-white font-heading font-bold uppercase tracking-wider px-8 py-4 rounded-full hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Call {PHONE}
              </a>
            </div>
            <p className="mt-6 text-slate-300/80 text-sm">
              Or email us anytime at <a href={`mailto:${EMAIL}`} className="underline hover:text-white">{EMAIL}</a>
            </p>
          </div>
        </section>
      </main>
    </>
  );
};

export default AboutPage;
